import { Button } from "@/components/ui/button";
import { ArrowLeft, Smartphone, Users, Zap, Shield, Globe, MessageSquare } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

const CommunityApp = () => {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-primary/30">
      <SEOHead
        title="Launch Your Own Community App"
        description="Stop relying on Facebook groups. Build a customized iOS & Android app for your community, complete with courses, live chats, and subscriptions."
        canonical="/community-app"
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-background/80 backdrop-blur-2xl border-b border-white/10 shadow-lg transition-all duration-300">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" className="text-xl font-light tracking-widest flex items-center gap-2 hover:opacity-80 transition-opacity">
            <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400">LRH SOLUTIONS</span>
          </a>
          <Button asChild variant="ghost" className="hover:bg-primary/10 hover:text-primary">
            <a href="/"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Home</a>
          </Button>
        </div>
      </nav>

      <div className="pt-32 pb-24 px-6 relative">
        {/* Abstract Background Elements */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] -z-10 pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] -z-10 pointer-events-none" />

        <div className="container mx-auto max-w-5xl text-center mb-16 animate-in fade-in slide-in-from-bottom-6 duration-700">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8 backdrop-blur-md">
            <span className="text-sm font-medium text-primary">Your Brand, Your Rules</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter mb-6">
            Launch Your Own <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400">Community App</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Stop relying on Facebook groups. Build a customized iOS & Android app for your community, complete with courses, live chats, and subscriptions.
          </p>
        </div>

        <div className="container mx-auto max-w-6xl grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            { icon: Smartphone, title: "Native iOS & Android", desc: "Your app in the Apple App Store and Google Play Store under your own brand." },
            { icon: Users, title: "Private Community", desc: "A dedicated space for your members to connect, share, and grow without algorithm distractions." },
            { icon: Zap, title: "Course Hosting", desc: "Upload and sell your courses directly within the app with seamless video playback." },
            { icon: MessageSquare, title: "Live Chat & DMs", desc: "Real-time communication channels, group chats, and direct messaging between members." },
            { icon: Globe, title: "Push Notifications", desc: "Reach your audience instantly with custom push notifications right to their phones." },
            { icon: Shield, title: "Monetization Ready", desc: "Charge monthly subscriptions or one-time fees directly through the app securely." }
          ].map((feature, i) => (
            <div key={i} className="p-8 rounded-2xl bg-card/50 backdrop-blur-md border border-white/5 hover:border-primary/50 transition-all duration-500 group hover:shadow-[0_0_30px_hsl(var(--primary)/0.15)] animate-in fade-in slide-in-from-bottom-8" style={{ animationDelay: `${i * 100}ms`, animationFillMode: 'forwards' }}>
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-[0_0_15px_hsl(var(--primary)/0.1)] group-hover:shadow-[0_0_30px_hsl(var(--primary)/0.3)]">
                <feature.icon className="w-7 h-7 text-primary group-hover:animate-pulse" />
              </div>
              <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.desc}</p>
            </div>
          ))}
        </div>

        <div className="container mx-auto max-w-4xl mt-24 text-center animate-in fade-in slide-in-from-bottom-8 duration-700 delay-500 fill-mode-both">
          <div className="p-12 rounded-3xl bg-gradient-to-tr from-primary/20 to-card border border-primary/30 shadow-[0_0_50px_hsl(var(--primary)/0.15)]">
            <h2 className="text-3xl font-black mb-4">Ready to build your empire?</h2>
            <p className="text-lg text-muted-foreground mb-8">Add the Community App to your package for just $200/mo.</p>
            <Button asChild size="lg" className="h-14 px-8 text-lg font-bold rounded-full relative group overflow-hidden bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_hsl(var(--primary)/0.4)] hover:shadow-[0_0_50px_hsl(var(--primary)/0.6)] transition-all duration-300">
              <a href="/#pricing">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer" />
                <span className="relative z-10">Get Started Today</span>
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityApp;
